import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { AssetPresentationsMenu } from 'features/Presentations/components/AssetPresentationsMenu'
import { AssetReportsButton } from 'features/Explorer/components/AssetReportsButton'
import SecondaryNavCN from 'components/SecondaryNavCN/SecondaryNavCN'
import { CompositeLocationAvatar } from 'components/CompositeLocationAvatar'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from 'components/ui/dropdown-menu'
import {
	ChevronDown,
	LayoutDashboard,
	Building2,
	BarChart3,
	DollarSign,
	GitCompareArrows,
	ShieldAlert,
	ListChecks,
	UserCheck,
	FileText,
	History,
	Puzzle,
} from 'lucide-react'
import { cn } from 'lib/utils'
import routes from 'constants/routes'
import { fetchEvaluationById, initializeEvaluations, updateEvaluation } from 'slices/evaluationSlice'
import EvaluationEditSheet from 'screens/evaluations/components/EvaluationEditSheet'

const STATUS_COLORS: Record<string, string> = {
	DRAFT: 'bg-gray-100 text-gray-700',
	IN_ANALYSIS: 'bg-blue-100 text-blue-700',
	UNDER_REVIEW: 'bg-amber-100 text-amber-700',
	APPROVED: 'bg-green-100 text-green-700',
	REJECTED: 'bg-red-100 text-red-700',
	ARCHIVED: 'bg-gray-200 text-gray-500',
}

const VALID_TRANSITIONS: Record<string, string[]> = {
	DRAFT: ['IN_ANALYSIS', 'ARCHIVED'],
	IN_ANALYSIS: ['UNDER_REVIEW', 'DRAFT', 'ARCHIVED'],
	UNDER_REVIEW: ['APPROVED', 'REJECTED', 'IN_ANALYSIS', 'ARCHIVED'],
	APPROVED: ['ARCHIVED'],
	REJECTED: ['DRAFT', 'ARCHIVED'],
	ARCHIVED: ['DRAFT'],
}

// Outlet context shape — child route components read this via useOutletContext().
// Stays loose-typed (`evaluation: any`) to match the existing Redux selectors,
// which the rest of the codebase types as `any`.
export interface EvaluationOutletContext {
	evaluation: any
	evalId: string
	evaluationStatus: string
}

const Evaluations_ShowCN = () => {
	const { id } = useParams<{ id: string }>()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const currentRoute = useLocation()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [backTitle, setBackTitle] = useState<string | null>(null)
	const [backLink, setBackLink] = useState<string | null>(null)
	const [editOpen, setEditOpen] = useState(false)

	const getBackLink = () => {
		const state = currentRoute.state as { backButtonTitle?: string; backButtonLink?: string } | null
		if (state) {
			if (backTitle === null) {
				setBackTitle(state.backButtonTitle ?? null)
				setBackLink(state.backButtonLink ?? null)
			}
			return { title: state.backButtonTitle ?? 'Evaluations', link: state.backButtonLink ?? routes.EVALUATIONS }
		}
		if (backTitle !== null) {
			return { title: backTitle, link: backLink ?? routes.EVALUATIONS }
		}
		return { title: 'Evaluations', link: routes.EVALUATIONS }
	}

	const evaluation = useSelector((state: any) => (id ? state.evaluations.entities[id] : null))
	const isInitialized = useSelector((state: any) => state.evaluations.isInitialized)

	useEffect(() => {
		if (id) {
			// @ts-ignore
			dispatch(fetchEvaluationById(id))
		}
		// Ensure list-aggregated fields (siteAvatars, siteCount) are in state
		// even on a direct deep-link to /evaluations/:id — the detail endpoint
		// doesn't return them, so without the list load the composite avatar
		// would render its empty state.
		if (!isInitialized) {
			// @ts-ignore
			dispatch(initializeEvaluations())
		}
	}, [dispatch, id, isInitialized])

	const handleStatusChange = useCallback(
		(newStatus: string) => {
			if (!id) return
			// @ts-ignore
			dispatch(updateEvaluation({ _id: id, status: newStatus }))
		},
		[dispatch, id]
	)

	const allowedTransitions = useMemo(() => (evaluation ? VALID_TRANSITIONS[evaluation.status] || [] : []), [evaluation])

	if (!evaluation || !id) {
		return (
			<div className="flex h-screen overflow-hidden bg-gray-50">
				<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
				<div className="flex-1 flex flex-col overflow-hidden">
					<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />
					<main className="flex-1 overflow-y-auto p-6">
						<div className="text-center py-20 text-sm text-gray-400">Loading evaluation...</div>
					</main>
				</div>
			</div>
		)
	}

	const outletContext: EvaluationOutletContext = {
		evaluation,
		evalId: id,
		evaluationStatus: evaluation.status,
	}

	const navLinks = [
		{ linkText: 'Overview', linkTo: `${routes.EVALUATIONS}/${id}`, icon: LayoutDashboard, end: true },
		{ linkText: 'Sites', linkTo: `${routes.EVALUATIONS}/${id}/sites`, icon: Building2 },
		{ linkText: 'Resources', linkTo: `${routes.EVALUATIONS}/${id}/resources`, icon: Puzzle },
		{ linkText: 'Scoring', linkTo: `${routes.EVALUATIONS}/${id}/scoring`, icon: BarChart3 },
		{ linkText: 'Financials', linkTo: `${routes.EVALUATIONS}/${id}/financials`, icon: DollarSign },
		{ linkText: 'Compare', linkTo: `${routes.EVALUATIONS}/${id}/compare`, icon: GitCompareArrows },
		{ linkText: 'Risks', linkTo: `${routes.EVALUATIONS}/${id}/risks`, icon: ShieldAlert },
		{ linkText: 'Pros / Cons', linkTo: `${routes.EVALUATIONS}/${id}/proscons`, icon: ListChecks },
		{ linkText: 'Approvals', linkTo: `${routes.EVALUATIONS}/${id}/approvals`, icon: UserCheck },
		{ linkText: 'Activity', linkTo: `${routes.EVALUATIONS}/${id}/activity`, icon: History },
		{ linkText: 'Reports', linkTo: `${routes.EVALUATIONS}/${id}/reports`, icon: FileText },
	]

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				{/* Detail header — standard MDT _Show pattern */}
				<div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2 flex items-center gap-3">
					<CompositeLocationAvatar
						locations={(evaluation?.siteAvatars || []).map((avatar: any) => ({
							imageUrl: avatar?.image,
							name: avatar?.name || '',
						}))}
						size="sm"
						className="rounded-full"
					/>
					<button
						type="button"
						onClick={() => setEditOpen(true)}
						className="font-semibold text-sm text-[#0080fe] bg-transparent border-none p-0 m-0 cursor-pointer hover:underline"
					>
						{evaluation.name}
					</button>
					{evaluation?.portfolio?.assetName && (
						<span className="text-sm text-gray-500">
							Portfolio: {evaluation.portfolio.assetName}
						</span>
					)}
					{evaluation?.marketArea && <span className="text-sm text-gray-500">Market: {evaluation.marketArea}</span>}
					<span className="text-sm text-gray-500">Evaluation Originator: {evaluation?.owner?.name}</span>

					<div className="ml-auto flex items-center gap-2">
						{/* Decks with a story about this evaluation (MYD-497). Owner-only by
						    construction: evaluations are never shared, so only the owner's
						    decks can anchor to one they can see. */}
						{/* Reports from a template, narrowed to this evaluation (MYD-598). */}
						<AssetReportsButton type="evaluation" entityId={id} name={evaluation?.name} />
						<AssetPresentationsMenu scope="evaluation" entityId={id} />
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									type="button"
									className={cn(
										'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
										STATUS_COLORS[evaluation.status],
										allowedTransitions.length > 0 && 'cursor-pointer hover:brightness-95'
									)}
									disabled={allowedTransitions.length === 0}
								>
									{evaluation.status.replace(/_/g, ' ')}
									{allowedTransitions.length > 0 && <ChevronDown className="w-3 h-3" />}
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{allowedTransitions.map(s => (
									<DropdownMenuItem key={s} onClick={() => handleStatusChange(s)}>
										{s.replace(/_/g, ' ')}
									</DropdownMenuItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				<SecondaryNavCN links={navLinks} backLink={getBackLink()} sourceName="Evaluations" />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<Outlet context={outletContext} />
					</main>
				</div>
			</div>

			{/* Right-hand edit drawer */}
			<EvaluationEditSheet
				open={editOpen}
				evaluation={evaluation}
				onClose={() => setEditOpen(false)}
				// The entity is gone from the store, so this route would fall
				// through to its "Loading evaluation…" state forever. `replace`
				// keeps the deleted evaluation out of the back history.
				onDeleted={() => navigate(routes.EVALUATIONS, { replace: true })}
			/>
		</div>
	)
}

export default Evaluations_ShowCN
